import React, { useState, useEffect } from "react";
import { fleetrifyIcons } from "../../assets/icons/icons";

type PinSetupProps = {
  pinData: {
    newPin: string;
    confirmNewPin: string;
  };
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBack: () => void;
  onNext: () => void;
};

const PinSetup: React.FC<PinSetupProps> = ({
  pinData,
  handleInputChange,
  onBack,
  onNext,
}) => {
  const [showNewPin, setShowNewPin] = useState(false);
  const [showConfirmNewPin, setShowConfirmNewPin] = useState(false);

  const [errors, setErrors] = useState<{
    [key in keyof PinSetupProps["pinData"]]: string;
  }>({
    newPin: "",
    confirmNewPin: "",
  });

  // Check if both PIN fields are filled and valid
  const isFormValid =
    pinData.newPin &&
    pinData.confirmNewPin &&
    pinData.newPin === pinData.confirmNewPin;

  // Toggle visibility of New PIN
  const toggleNewPinVisibility = () => {
    setShowNewPin((prev) => !prev);
  };

  // Toggle visibility of Confirm New PIN
  const toggleConfirmNewPinVisibility = () => {
    setShowConfirmNewPin((prev) => !prev);
  };

  // Handle input change and clear errors when user types
  const handlePinInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Only allow numeric input
    if (/^\d*$/.test(value)) {
      handleInputChange(e);
    }

    // Clear errors if the input is not empty
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: value ? "" : prevErrors[name as keyof typeof prevErrors],
    }));
  };

  // Handle blur to show errors when the field is left empty
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (!value) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: "This field is required.",
      }));
    }
  };

  // Handle validation for PINs matching
  const handlePinValidation = () => {
    if (
      pinData.newPin &&
      pinData.confirmNewPin &&
      pinData.newPin !== pinData.confirmNewPin
    ) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        confirmNewPin: "PINs do not match.",
      }));
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        confirmNewPin: "",
      }));
    }
  };

  useEffect(() => {
    handlePinValidation(); // Run validation on mount and whenever the PIN fields change
  }, [pinData.newPin, pinData.confirmNewPin]);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-8">
      <div className="mb-8">
        <h2 className="text-[16px] [16px]:text-[20px] font-semibold text-fleetblue mb-2">
          Setup Wallet PIN
        </h2>
        <p className="text-gray-500 text-[16px] mt-3 border-b border-[#E0E0E0] pb-3">
          Enter your 4-digit PIN
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* New PIN */}
        <div>
          <label className="block text-[16px] font-medium text-fleetdark mb-2">
            New PIN:<span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type={showNewPin ? "text" : "password"}
              name="newPin"
              maxLength={4}
              value={pinData.newPin}
              onChange={handlePinInputChange}
              onBlur={handleBlur}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none"
              placeholder="4-digit input number only"
            />
            <button
              type="button"
              className="absolute right-4 top-1/2 transform -translate-y-1/2"
              onClick={toggleNewPinVisibility}
            >
              {showNewPin ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  className="w-6 h-6 text-gray-500"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 12c0-1.657-1.343-3-3-3s-3 1.343-3 3 1.343 3 3 3 3-1.343 3-3z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M2.458 12C3.732 7.943 7.898 5 12 5c4.102 0 8.268 2.943 9.542 7-1.274 4.057-5.44 7-9.542 7-4.102 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              ) : (
                fleetrifyIcons.closeIcon
              )}
            </button>
          </div>
          {errors.newPin && (
            <p className="text-red-500 text-sm mt-1">{errors.newPin}</p>
          )}
        </div>

        {/* Confirm New PIN */}
        <div>
          <label className="block text-[16px] font-medium text-fleetdark mb-2">
            Confirm New PIN:<span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type={showConfirmNewPin ? "text" : "password"}
              name="confirmNewPin"
              maxLength={4}
              value={pinData.confirmNewPin}
              onChange={handlePinInputChange}
              onBlur={handleBlur}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg outline-none"
              placeholder="4-digit input number only"
            />
            <button
              type="button"
              className="absolute right-4 top-1/2 transform -translate-y-1/2"
              onClick={toggleConfirmNewPinVisibility}
            >
              {showConfirmNewPin ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  className="w-6 h-6 text-gray-500"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 12c0-1.657-1.343-3-3-3s-3 1.343-3 3 1.343 3 3 3 3-1.343 3-3z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M2.458 12C3.732 7.943 7.898 5 12 5c4.102 0 8.268 2.943 9.542 7-1.274 4.057-5.44 7-9.542 7-4.102 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              ) : (
                fleetrifyIcons.closeIcon
              )}
            </button>
          </div>
          {errors.confirmNewPin && (
            <p className="text-red-500 text-sm mt-1">{errors.confirmNewPin}</p>
          )}
        </div>
      </div>

      {/* Buttons */}
      <div className="flex justify-end gap-4 mt-10">
        {/* Back Button */}
        <button
          className="px-4 py-2 bg-transparent text-fleetblue rounded-md flex items-center border border-fleetblue"
          onClick={onBack}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            className="w-6 h-6 mr-2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M19 12H5m0 0l7-7m-7 7l7 7"
            />
          </svg>
          Back
        </button>

        {/* Next Button */}
        <button
          className={`px-6 py-2 rounded-[8px] flex items-center justify-center ${
            isFormValid
              ? "bg-fleetblue text-white"
              : "bg-fleetinactiveblue text-[#D0D7E7]"
          }`}
          onClick={onNext}
          disabled={!isFormValid}
        >
          Next: Link Account
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            className="w-6 h-6 ml-2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M5 12h14m0 0l-5-5m5 5l-5 5"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default PinSetup;
